import { NextQuery, RelatedTag } from '@empathy/search-types';
import { Observable } from 'rxjs/Observable';
import { filter } from 'rxjs/operators/filter';
import { map } from 'rxjs/operators/map';
import { EVENTS } from '../../bus/events.const';
import { EmpathyXConfig } from '../../models/config';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store';
import { SetNextQueriesStatePayload } from '../../store/query-signals/mutations/set-query-signals.mutation';
import { catchAdapterError } from '../../utils';
import { hasSearchQuery } from '../pipes/has-search-query.operator';
import { createWires } from '../wiring.util';
import { WIRE_FACTORY } from './wires.factory';

export const QUERY_SIGNALS_WIRES = createWires({
  getRelatedTags(observable, { store, emit }) {
    observable
      .pipe(filter(() => !!store.state.core.inputQuery.trim()))
      .subscribe(() => store.dispatch(ACTIONS_KEYS.querySignals.getRelatedTags)
        .then((relatedTags: RelatedTag[]) => emit(EVENTS.RelatedTagsLoadSuccess, relatedTags))
        .catch(error => {
          catchAdapterError('Related tags')(error);
          store.commit(MUTATIONS_KEYS.querySignals.clearRelatedTags);
        })
      );
  },
  getNextQueries(observable, { store, emit }) {
    observable
      .pipe(hasSearchQuery(store))
      .subscribe(query => store.dispatch(ACTIONS_KEYS.querySignals.getNextQueries, query)
        .then((nextQueriesState: SetNextQueriesStatePayload) => emit(EVENTS.NextQueriesLoadSuccess, nextQueriesState))
        .catch(error => {
          catchAdapterError('NextQueries')(error);
          store.commit(MUTATIONS_KEYS.querySignals.setNextQueriesState, { nextQueries: [], nextQueriesQuery: query });
        })
      );
  },
  getNextQueriesOfSessionQueryHistory(observable: Observable<NextQuery[]>, { store, emit }) {
    observable
      .pipe(
        map(() => store.state.history.sessionQueryHistory[0]),
        filter(query => !!query))
      .subscribe(query => store.dispatch(ACTIONS_KEYS.querySignals.getNextQueries, query)
        .then((nextQueriesState: SetNextQueriesStatePayload) => emit(EVENTS.NextQueriesLoadSuccess, nextQueriesState))
        .catch(error => {
          catchAdapterError('NextQueries')(error);
          store.commit(MUTATIONS_KEYS.querySignals.setNextQueriesState, { nextQueries: [], nextQueriesQuery: query });
        })
      );
  },
  applyRelatedTag(observable: Observable<RelatedTag>, { emit }) {
    observable.subscribe(relatedTag => emit(EVENTS.QueryChanged, relatedTag.query));
  },
  applyNextQuery(observable: Observable<NextQuery>, { emit, store }) {
    observable.subscribe(nextQuery => {
      store.commit(MUTATIONS_KEYS.search.setFacets, nextQuery.resultsFacets);
      emit(EVENTS.QueryChanged, nextQuery.query);
    });
  },
  saveNextQueriesState: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.setNextQueriesState),
  saveRelatedTags: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.setRelatedTags),
  loadNextQueriesResultsIfEnabled: WIRE_FACTORY.dispatchActionConditionally(
    { ifAction: ACTIONS_KEYS.querySignals.getNextQueriesResults },
    (payload, store) => (store.getters[GETTERS_KEYS.config.config] as EmpathyXConfig).nextQueries.withResults),
  clearRelatedTags: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.clearRelatedTags),
  deselectRelatedTags: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.deselectRelatedTags),
  clearNextQueriesState: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.clearNextQueriesState),
  emitApplyNextQuery: WIRE_FACTORY.emitEvent(EVENTS.ApplyNextQuery),
  toggleRelatedTag: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.toggleRelatedTag),
  resetNextQueryResults: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.querySignals.resetNextQueryResults, undefined)
});
